import { Injectable } from '@angular/core';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

/**
 * Pads a number with a leading zero if it is less than 10.
 * @param value The number to be padded.
 * @returns The padded number as a string.
 */
function padNumber(value: number) {
    if (isNumber(value)) {
        return `0${value}`.slice(-2);
    } else {
        return '';
    }
}

/**
 * Checks if the provided value is a number.
 * @param value The value to be checked.
 * @returns A boolean indicating whether the value is a number.
 */
function isNumber(value: any): boolean {
    return !isNaN(toInteger(value));
}

/**
 * Converts the given value to an integer.
 * @param value The value to be converted to an integer.
 * @returns The integer value of the given value.
 */
function toInteger(value: any): number {
    return parseInt(`${value}`, 10);
}

/**
 * Adapter class for the NgbDateParserFormatter.
 * Converts a date string into a NgbDateStruct object and vice versa.
 */
@Injectable()
/**
 * NgbDatePickerAdapter
 * @class NgbDatePickerAdapter
 * @extends {NgbDateParserFormatter}
 */
export class NgbDatePickerAdapter extends NgbDateParserFormatter {
    /**
     * Parses the given date string into a NgbDateStruct object.
     * The date can be in thoose formats:
     *   - 'yyyy'
     *   - 'mm/yyyy'
     *   - 'dd/mm/yyyy'
     * @param value The date string to be parsed.
     * @returns A NgbDateStruct object or null if the date string could not be parsed.
     */
    parse(value: string): NgbDateStruct {
        if (value) {
            const dateParts = value.trim().split('/');
            if (dateParts.length === 1 && isNumber(dateParts[0])) {
                return { year: toInteger(dateParts[0]), month: null, day: null };
            } else if (dateParts.length === 2 && isNumber(dateParts[0]) && isNumber(dateParts[1])) {
                return { year: toInteger(dateParts[1]), month: toInteger(dateParts[0]), day: null };
            } else if (dateParts.length === 3 && isNumber(dateParts[0]) && isNumber(dateParts[1]) && isNumber(dateParts[2])) {
                return { year: toInteger(dateParts[2]), month: toInteger(dateParts[1]), day: toInteger(dateParts[0]) };
            }
        }
        return null;
    }

    /**
     * Formats a NgbDateStruct object into a date string.
     * The format of the date string is 'dd/mm/yyyy', where 'dd' and 'mm'
     * are zero-padded if necessary. If the day or month are not provided, they
     * will be omitted from the formatted string.
     * @param date - The NgbDateStruct object to format.
     * @returns A formatted date string or an empty string if the date is not valid.
     */
    format(date: NgbDateStruct): string {
        let stringDate = '';
        if (date) {
            stringDate += isNumber(date.day) ? padNumber(date.day) + '/' : '';
            stringDate += isNumber(date.month) ? padNumber(date.month) + '/' : '';
            stringDate += date.year;
        }
        return stringDate;
    }
}
